import { formatDistanceToNow, format } from 'date-fns'
import { string, bool, func, array } from 'prop-types'
import { useState } from 'react'
import {
  Button,
  kuiperClassNames,
  Conditional,
  ConnectedAvatar,
  DraftJSEditorInput,
  WithTooltip,
} from '@opallabs/kuiper'
import { styles } from './styles.js'

import './styles.scss'

export const DATE_FORMAT = 'MMM d yyyy, h:mm a'
export const onMessageChange = ({
  popoverShouldClose,
  setText,
  togglePopoverShouldClose,
  value,
}) => {
  if (value && popoverShouldClose) {
    togglePopoverShouldClose(false)
  }
  if (!value && !popoverShouldClose) {
    togglePopoverShouldClose(true)
  }

  setText(value)
}

const fullTimestamp = (createdAt, userTimezone) => {
  const date = createdAt ? new Date(createdAt) : new Date()
  return `${format(date, DATE_FORMAT)} ${userTimezone}`
}

const truncatedTimestamp = (createdAt) => {
  const date = createdAt ? new Date(createdAt) : new Date()
  return formatDistanceToNow(new Date(date), { addSuffix: true })
}

const AnnotationMessage = ({
  message,
  mentionableUsers,
  createdAt,
  togglePopoverShouldClose,
  editing = false,
  isCurrentUser = false,
  onCancel,
  onDelete,
  onSubmit,
  popoverShouldClose,
  readOnly = false,
  userId,
  userName,
  userTimezone,
  viewUserProfile,
}) => {
  const [messageText, setText] = useState(message || '')
  return (
    <div className={kuiperClassNames('annotation-message')}>
      <div className={kuiperClassNames('avatar')}>
        <ConnectedAvatar userId={userId} size={36} />
      </div>
      <Conditional condition={editing}>
        <div className={kuiperClassNames('message-body')}>
          <DraftJSEditorInput
            focusOnMount={true}
            mentionable={true}
            mentionableUsers={mentionableUsers}
            mentionStyle={styles.mentions}
            style={styles.activeInput}
            text={messageText}
            onChange={(value) =>
              onMessageChange({
                value,
                popoverShouldClose,
                togglePopoverShouldClose,
                setText,
              })
            }
          />
        </div>
      </Conditional>
      <Conditional condition={!editing}>
        <div className={kuiperClassNames('message-body')}>
          <div className={kuiperClassNames('message-meta')}>
            <span>{userName}</span>
            <WithTooltip
              inline
              content={fullTimestamp(createdAt, userTimezone)}
            >
              <time>{truncatedTimestamp(createdAt)}</time>
            </WithTooltip>
            <Conditional condition={isCurrentUser && !readOnly}>
              <WithTooltip
                className="delete-message"
                content="Delete annotation"
                tooltipOptions={{ align: 'end' }}
              >
                <Button
                  icon="delete"
                  hideLabelText={true}
                  label="open delete modal"
                  onClick={onDelete}
                  type="invisible"
                />
              </WithTooltip>
            </Conditional>
          </div>
          <DraftJSEditorInput
            mentionable={true}
            mentionableUsers={mentionableUsers}
            style={styles.inactiveInput}
            readOnly={true}
            text={message}
            viewUserProfile={viewUserProfile}
          />
        </div>
      </Conditional>
      <Conditional condition={editing}>
        <div className={kuiperClassNames('controls-container')}>
          <span className={kuiperClassNames('subscribed-text')}>
            Subscribed users will be notified.
          </span>
          <div className={kuiperClassNames('buttons-container')}>
            <Button label="Cancel" onClick={onCancel} type="nobutton" />
            <Button
              label="Submit"
              disabled={!messageText.trim()}
              onClick={() => onSubmit(messageText.trim())}
              type="primary"
            />
          </div>
        </div>
      </Conditional>
    </div>
  )
}

AnnotationMessage.propTypes = {
  createdAt: string,
  editing: bool,
  isCurrentUser: bool,
  mentionableUsers: array,
  message: string,
  onCancel: func,
  onDelete: func,
  onSubmit: func,
  popoverShouldClose: bool,
  readOnly: bool,
  togglePopoverShouldClose: func,
  userId: string,
  userName: string,
  userTimezone: string,
  viewUserProfile: func,
}

export default AnnotationMessage
